import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  query,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  Timestamp,
  getDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { RouteGroup, Route, Client, Order, InventoryItem } from '../models';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Paper,
  CircularProgress,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormControlLabel,
  Tooltip,
  FormGroup,
  TablePagination,
  TableSortLabel,
  Card,
  CardContent,
  CardActions,
  Avatar,
  SvgIcon,
  SvgIconProps,
  Chip,
  Switch,
  useTheme,
  useMediaQuery,
  FormLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday,
  Today,
  PendingActions,
  CheckCircle,
  Cancel,
  FilterList as FilterListIcon,
  ViewColumn as ViewColumnIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  GetApp as GetAppIcon,
  FormatListBulleted,
} from '@mui/icons-material';
import OrderCreationModalAutomatic from './OrderCreationModalAutomatic';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';


// Define styled components
const DarkOverlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(4px)',
  zIndex: 9998,
  animation: 'fadeIn 0.3s ease-out',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
});

const NotificationPopup = styled(Box)({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  padding: '32px 48px',
  borderRadius: '24px',
  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.25)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
  zIndex: 9999,
  // minWidth: '500px',
  animation: 'slideIn 0.5s ease-out',
  '@keyframes slideIn': {
    '0%': {
      transform: 'translate(-50%, -40%)',
      opacity: 0,
    },
    '100%': {
      transform: 'translate(-50%, -50%)',
      opacity: 1,
    },
  },
});

const AnimatedEmoji = styled('span')({
  display: 'inline-block',
  animation: 'bounce 1s ease infinite',
  fontSize: '2.5rem',
  '@keyframes bounce': {
    '0%, 100%': {
      transform: 'translateY(0)',
    },
    '50%': {
      transform: 'translateY(-8px)',
    },
  },
});

const columnNames = {
  name: 'Nombre',
  routeType: 'Tipo de Ruta',
  // createdAt: 'Fecha de Entrega',
};

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#00b4d8',
    },
    '&:hover fieldset': {
      borderColor: '#00b4d8',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00b4d8',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#00b4d8',
  },
}));

const InfoSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundColor: '#f5f5f5',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  color: '#00b4d8',
}));

const IconWrapper = styled('span')(({ theme }) => ({
  marginRight: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${theme.breakpoints.down('sm')}`]: {
    display: 'none',
  },
}));

// Definir los iconos personalizados
const OllinIcon: React.FC<SvgIconProps> = (props) => (
  <SvgIcon {...props}>
    <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2M1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2" />
  </SvgIcon>
);

const RecoleccionIcon: React.FC<SvgIconProps> = (props) => (
  <SvgIcon {...props} viewBox="0 0 512 512">
    <path d="M184.561 261.903c3.232 13.997-12.123 24.635-24.068 17.168l-40.736-25.455-50.867 81.402C55.606 356.273 70.96 384 96.012 384H148c6.627 0 12 5.373 12 12v40c0 6.627-5.373 12-12 12H96.115c-75.334 0-121.302-83.048-81.408-146.88l50.822-81.388-40.725-25.448c-12.081-7.547-8.966-25.961 4.879-29.158l110.237-25.45c8.611-1.988 17.201 3.381 19.189 11.99l25.452 110.237zm98.561-182.915l41.289 66.076-40.74 25.457c-12.051 7.528-9 25.953 4.879 29.158l110.237 25.45c8.672 1.999 17.215-3.438 19.189-11.99l25.45-110.237c3.197-13.844-11.99-24.719-24.068-17.168l-40.687 25.424-41.263-66.082c-37.521-60.033-125.209-60.171-162.816 0l-17.963 28.766c-3.51 5.62-1.8 13.021 3.82 16.533l33.919 21.195c5.62 3.512 13.024 1.803 16.536-3.817l17.961-28.743c12.712-20.341 41.973-19.676 54.257-.022zM497.288 301.12l-27.515-44.065c-3.511-5.623-10.916-7.334-16.538-3.821l-33.861 21.159c-5.62 3.512-7.33 10.915-3.818 16.536l27.564 44.112c13.257 21.211-2.057 48.96-27.136 48.96H320V336.02c0-14.213-17.242-21.383-27.313-11.313l-80 79.981c-6.249 6.248-6.249 16.379 0 22.627l80 79.989C302.689 517.308 320 510.3 320 495.989V448h95.88c75.274 0 121.335-82.997 81.408-146.88z" />
  </SvgIcon>
);

const startOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

const endOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

const RouteGroupsCRUD: React.FC = () => {
  const [routesUnion, setRoutesUnion] = useState<{ routeUnion: { routes: Route[], date: Date, isUnificationRoute: boolean }[], groupName: string, groupId: string, routeType: string, dayOfWeek: number, weekOfMonth: number, createdAt: Date, unificationFlag: boolean }[]>([]);
  const [open, setOpen] = useState(false);
  const [currentRouteGroup, setCurrentRouteGroup] = useState<RouteGroup | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [currentRoute, setCurrentRoute] = useState<Route | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [columnSelectionDialogOpen, setColumnSelectionDialogOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set([
    'name', 'routeType', 'dayOfWeek', 'weekOfMonth', 'createdAt', 'nextDelivery'
  ]));
  const [filters, setFilters] = useState<{ [key: string]: any }>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof RouteGroup>('name');
  const [tabValue, setTabValue] = useState(0);
  const [selectedRouteTypes, setSelectedRouteTypes] = useState<string[]>([]); // Añadir este estado
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    fetchRouteGroups();
    fetchClients();
    fetchInventoryItems();
  }, []);

  const fetchRouteGroups = async () => {
    setLoading(true);
    try {
      // Calculate date range (tomorrow to one month in the future)
      const currentDate = new Date();
      const startDate = new Date(currentDate);
      startDate.setDate(startDate.getDate() + 1);
      const oneMonthFromNow = new Date(startDate);
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

      const q = query(collection(db, 'routeGroups'));
      const querySnapshot = await getDocs(q);
      const allRoutesUnion: { routeUnion: { routes: Route[], date: Date, isUnificationRoute: boolean }[], groupName: string, groupId: string, routeType: string, dayOfWeek: number, weekOfMonth: number, createdAt: Date, unificationFlag: boolean }[] = [];

      for (const docRef of querySnapshot.docs) {
        const groupData = docRef.data();

        // Skip RouteGroups with unificationFlag set to true
        if (groupData.unificationFlag) {
          continue;
        }

        const routesUnionQuery = query(
          collection(db, 'routeGroups', docRef.id, 'routesUnion'),
          where('date', '>=', startDate),
          where('date', '<=', oneMonthFromNow)
        );
        const routesUnionSnapshot = await getDocs(routesUnionQuery);
        const routesUnion = routesUnionSnapshot.docs.map(routeDoc => {
          const data = routeDoc.data();
          return {
            routes: [{ id: routeDoc.id, ...data.route } as Route],
            date: data.date.toDate(),
            isUnificationRoute: data.isUnificationRoute
          };
        });

        routesUnion.forEach(routeUnion => {
          allRoutesUnion.push({
            routeUnion: [{ routes: routeUnion.routes, date: routeUnion.date, isUnificationRoute: routeUnion.isUnificationRoute }],
            groupName: groupData.name,
            groupId: docRef.id,
            routeType: groupData.routeType || 'monthly',
            dayOfWeek: groupData.dayOfWeek,
            weekOfMonth: groupData.weekOfMonth,
            createdAt: groupData.createdAt.toDate(),
            unificationFlag: groupData.unificationFlag
          });
        });
      }

      setRoutesUnion(allRoutesUnion);
    } catch (error) {
      // console.error("Error fetching route groups: ", error);
      toast.error('Error fetching route groups');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const q = query(collection(db, 'clients'));
      const querySnapshot = await getDocs(q);
      setClients(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Client)));
    } catch (error) {
      // console.error("Error fetching clients: ", error);
      toast.error('Error fetching clients');
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const q = query(collection(db, 'inventoryItems'));
      const querySnapshot = await getDocs(q);
      setInventoryItems(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as InventoryItem)));
    } catch (error) {
      // console.error("Error fetching inventory items: ", error);
      toast.error('Error fetching inventory items');
    }
  };

  const handleOpen = (routeGroup: RouteGroup | null = null) => {
    setCurrentRouteGroup(routeGroup || {
      name: '',
      dayOfWeek: 1,
      weekOfMonth: 1,
      createdAt: new Date(),
      routes: [],
      routesTemp: [],
      routesUnion: [],
      routeType: 'weekly'
    } as RouteGroup);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentRouteGroup(null);
  };

  const handleRouteTypeChange = (value: 'weekly' | 'biweekly' | 'monthly' | 'bimonthly') => {
    if (!currentRouteGroup) return;

    let updatedGroup: RouteGroup = {
      ...currentRouteGroup,
      routeType: value,
      dayOfWeek: value === 'monthly' || value === 'bimonthly' ? (currentRouteGroup.dayOfWeek || 1) : 0,
      weekOfMonth: value === 'monthly' || value === 'bimonthly' ? (currentRouteGroup.weekOfMonth || 1) : 0,
      createdAt: value === 'monthly' || value === 'bimonthly' ? new Date() : (currentRouteGroup.createdAt || new Date()),
    };
    setCurrentRouteGroup(updatedGroup);
  };

  const handleCreatedAtChange = (date: Date | null) => {
    if (!currentRouteGroup || !date) return;
    setCurrentRouteGroup({
      ...currentRouteGroup,
      createdAt: date
    });
  };

  const handleSave = async () => {
    if (!currentRouteGroup || !isFormValid()) return;

    try {
      const batch = writeBatch(db);

      const routeGroupData = {
        name: currentRouteGroup.name,
        routeType: currentRouteGroup.routeType,
        dayOfWeek: currentRouteGroup.dayOfWeek,
        weekOfMonth: currentRouteGroup.weekOfMonth,
        createdAt: Timestamp.fromDate(currentRouteGroup.createdAt || new Date())
      };

      if (currentRouteGroup.id) {
        const routeGroupRef = doc(db, 'routeGroups', currentRouteGroup.id);
        batch.update(routeGroupRef, routeGroupData);

        const routesRef = collection(routeGroupRef, 'routes');
        const existingRoutes = await getDocs(routesRef);

        existingRoutes.forEach((doc) => {
          batch.delete(doc.ref);
        });

        currentRouteGroup.routes.forEach((route) => {
          const newRouteRef = doc(routesRef);
          batch.set(newRouteRef, {
            clientId: route.clientId,
            status: route.status,
            order: route.order
          });
        });
      } else {
        const newRouteGroupRef = doc(collection(db, 'routeGroups'));
        batch.set(newRouteGroupRef, routeGroupData);

        const routesRef = collection(newRouteGroupRef, 'routes');
        currentRouteGroup.routes.forEach((route) => {
          const newRouteRef = doc(routesRef);
          batch.set(newRouteRef, {
            clientId: route.clientId,
            status: route.status,
            order: route.order
          });
        });
      }

      await batch.commit();
      toast.success(currentRouteGroup.id ? 'Grupo de rutas actualizado exitosamente' : 'Nuevo grupo de rutas creado exitosamente');
      handleClose();
      fetchRouteGroups();

    } catch (error) {
      // console.error("Error saving route group: ", error);
      toast.error('Error al guardar el grupo de rutas');
    }
  };

  const handleDelete = async (id: string) => {
    setGroupToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!groupToDelete) return;

    try {
      const batch = writeBatch(db);
      const routeGroupRef = doc(db, 'routeGroups', groupToDelete);
      const routesRef = collection(routeGroupRef, 'routes');
      const routesSnapshot = await getDocs(routesRef);

      routesSnapshot.docs.forEach((routeDoc) => {
        batch.delete(routeDoc.ref);
      });

      batch.delete(routeGroupRef);

      await batch.commit();
      fetchRouteGroups();
      toast.success('Grupo de rutas eliminado exitosamente');
    } catch (error) {
      // console.error("Error al eliminar el grupo de rutas: ", error);
      toast.error('Error al eliminar el grupo de rutas');
    } finally {
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
    }
  };

  const removeRoute = (index: number) => {
    if (!currentRouteGroup) return;
    const updatedRoutes = currentRouteGroup.routes.filter((_, i) => i !== index);
    setCurrentRouteGroup({ ...currentRouteGroup, routes: updatedRoutes });
    toast.info('Ruta eliminada');
  };

  const handleRouteChange = (index: number, field: keyof Route, value: any) => {
    if (!currentRouteGroup) return;
    const updatedRoutes = [...currentRouteGroup.routes];
    updatedRoutes[index] = { ...updatedRoutes[index], [field]: value };
    setCurrentRouteGroup({ ...currentRouteGroup, routes: updatedRoutes });
  };

  const addRoute = () => {
    if (!currentRouteGroup) return;
    const newRoute: Route = {
      clientId: '',
      status: 'pending',
      order: { items: [] },
      tipo_pedido: 'Automatizado',
      fecha_pedido: new Date(),
      routeGroupId: currentRouteGroup.id || ''
    };
    setCurrentRouteGroup({
      ...currentRouteGroup,
      routes: [...currentRouteGroup.routes, newRoute]
    });
  };

  const handleCreateOrEditOrder = (routeGroup: RouteGroup, route: Route) => {
    setCurrentRouteGroup(routeGroup);
    setCurrentRoute(route);
    setIsOrderModalOpen(true);
  };

  const handleOrderCreated = async (order: Order) => {
    if (currentRouteGroup && currentRoute) {
      try {
        const updatedRoute: Route = {
          ...currentRoute,
          order: order,
          status: 'pending'
        };

        const updatedRoutes = currentRouteGroup.routes.map(r =>
          r === currentRoute ? updatedRoute : r
        );

        setCurrentRouteGroup({
          ...currentRouteGroup,
          routes: updatedRoutes
        });

        const updatedInventoryItems = [...inventoryItems];
        for (const item of order.items) {
          const inventoryItem = updatedInventoryItems.find(i => i.id === item.inventoryItemId);
          if (inventoryItem) {
            inventoryItem.quantity -= item.quantity;
          }
        }
        setInventoryItems(updatedInventoryItems);

        setIsOrderModalOpen(false);
        setCurrentRoute(null);
        toast.success('Orden creada exitosamente');
      } catch (error) {
        // console.error("Error updating route group with new order: ", error);
        toast.error('Error al actualizar el grupo de rutas con la nueva orden');
      }
    }
  };

  const isFormValid = () => {
    if (!currentRouteGroup) return false;
    return (
      currentRouteGroup.name.trim() !== '' &&
      currentRouteGroup.routes.length > 0 &&
      currentRouteGroup.routes.every(route =>
        route.clientId && (
          (route.order && route.order.items && route.order.items.some(item => item.quantity > 0)) ||
          route.status === 'pending'
        )
      )
    );
  };

  const getDayOfWeekName = (day: number = 0) => {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return days[day];
  };

  const calculateNextDeliveryDate = (group: RouteGroup): Date => {
    const today = new Date();
    let nextDelivery = new Date(group.createdAt);

    switch (group.routeType) {
      case 'weekly':
        while (nextDelivery <= today) {
          nextDelivery.setDate(nextDelivery.getDate() + 7);
        }
        break;
      case 'biweekly':
        while (nextDelivery <= today) {
          nextDelivery.setDate(nextDelivery.getDate() + 14);
        }
        break;
      case 'monthly':
      case 'bimonthly':
        while (nextDelivery <= today) {
          nextDelivery.setMonth(nextDelivery.getMonth() + (group.routeType === 'monthly' ? 1 : 2));
          nextDelivery.setDate(1);
          while (nextDelivery.getDay() !== group.dayOfWeek) {
            nextDelivery.setDate(nextDelivery.getDate() + 1);
          }
          nextDelivery.setDate(nextDelivery.getDate() + ((group.weekOfMonth ?? 1) - 1) * 7);
        }
        break;
    }

    return nextDelivery;
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterDialogOpen = () => {
    setFilterDialogOpen(true);
  };

  const handleFilterDialogClose = () => {
    setFilterDialogOpen(false);
  };

  const handleColumnSelectionDialogOpen = () => {
    setColumnSelectionDialogOpen(true);
  };

  const handleColumnSelectionDialogClose = () => {
    setColumnSelectionDialogOpen(false);
  };

  const handleColumnToggle = (column: string) => {
    setVisibleColumns(prevColumns => {
      const newColumns = new Set(prevColumns);
      if (newColumns.has(column)) {
        newColumns.delete(column);
      } else {
        newColumns.add(column);
      }
      return newColumns;
    });
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [field]: value
    }));
    // Actualiza las selecciones de tipo de ruta
    if (field === 'routeType') {
      setSelectedRouteTypes(value);
    }
  };

  const applyFilters = () => {
    // Apply filters logic here
    handleFilterDialogClose();
  };

  const filteredRouteGroups = useMemo(() => {
    const allRoutesUnion = routesUnion.flatMap(group =>
      group.routeUnion.map(routeUnion => ({
        route: routeUnion.routes, // Array of Route objects
        date: routeUnion.date, // Date object
        isUnificationRoute: routeUnion.isUnificationRoute, // Boolean
        groupName: group.groupName,
        groupId: group.groupId,
        routeType: group.routeType,
        dayOfWeek: group.dayOfWeek,
        weekOfMonth: group.weekOfMonth,
        createdAt: group.createdAt,
        name: group.groupName, // Add name property
        unificationFlag: group.unificationFlag,
      }))
    );


    const sortedGroups = [...allRoutesUnion].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Manejo de la columna "Siguiente Entrega"
      if (orderBy === 'nextDelivery' as keyof RouteGroup) {
        aValue = a.date.getTime(); // Convertir a timestamp
        bValue = b.date.getTime(); // Convertir a timestamp  
      } else {
        aValue = (a as any)[orderBy];
        bValue = (b as any)[orderBy];
      }

      if (aValue === undefined || bValue === undefined) {
        return 0; // Si alguno es undefined, no se cambia el orden
      }

      if (order === 'desc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0; // Comparación para orden ascendente
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0; // Comparación para orden descendente
      }
    });
    return sortedGroups.filter(group => {
      const matchesRouteType = selectedRouteTypes.length === 0 || selectedRouteTypes.includes(group.routeType || '');
      const matchesSearchTerm = (
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (group.routeType && group.routeType.toLowerCase().includes(searchTerm.toLowerCase())) ||
        getDayOfWeekName(group.dayOfWeek).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (group.weekOfMonth ?? '').toString().includes(searchTerm) ||
        group.createdAt.toLocaleDateString().includes(searchTerm)
        //  ||
        // calculateNextDeliveryDate(group).toLocaleDateString().includes(searchTerm)
      );
      const hasUnificationFlagUndefinedOrFalse = group.unificationFlag === undefined || group.unificationFlag === false; // Nueva condición
      return matchesRouteType && matchesSearchTerm && hasUnificationFlagUndefinedOrFalse; // Filtra por tipo de ruta, término de búsqueda y unificationFlag
    });
  }, [routesUnion, selectedRouteTypes, searchTerm, order, orderBy]); // Añadir order y orderBy como dependencias

  const filteredRoutesUnion = useMemo(() => {
    const allRoutesUnion = routesUnion.flatMap(group =>
      group.routeUnion.map(routeUnion => ({
        ...routeUnion,
        groupName: group.groupName,
        groupId: group.groupId,
        routeType: group.routeType,
        dayOfWeek: group.dayOfWeek,
        weekOfMonth: group.weekOfMonth,
        createdAt: group.createdAt,
      }))
    );

    const filteredBySearchTerm = allRoutesUnion.filter(routeUnion =>
      routeUnion.groupName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredByDialog = filteredBySearchTerm.filter(routeUnion => {
      const matchesRouteType = selectedRouteTypes.length === 0 || selectedRouteTypes.includes(routeUnion.routeType || '');
      return matchesRouteType;
    });

    const sortedRoutesUnion = [...filteredByDialog].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (orderBy === 'nextDelivery' as keyof RouteGroup) {
        aValue = a.date.getTime();
        bValue = b.date.getTime();
      } else {
        aValue = (a as any)[orderBy];
        bValue = (b as any)[orderBy];
      }

      if (aValue === undefined || bValue === undefined) {
        return 0;
      }

      if (order === 'desc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return sortedRoutesUnion;
  }, [routesUnion, searchTerm, selectedRouteTypes, order, orderBy]);

  const handlePrint = () => {
    window.print();
  };

  // const handleDownloadCSV = () => {
  //   const headers = Array.from(visibleColumns);
  //   const csvContent = [
  //     headers.join(','),
  //     ...filteredRouteGroups.map(group =>
  //       headers.map(header => {
  //         if (header === 'nextDelivery') {
  //           return calculateNextDeliveryDate(group).toLocaleDateString();
  //         }
  //         return ((group as unknown as RouteGroup)[header as keyof RouteGroup] || '').toString();
  //       }).join(',')
  //     )
  //   ].join('\n');

  //   const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  //   const link = document.createElement('a');
  //   if (link.download !== undefined) {
  //     const url = URL.createObjectURL(blob);
  //     link.setAttribute('href', url);
  //     link.setAttribute('download', 'route_groups.csv');
  //     link.style.visibility = 'hidden';
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //   }
  // };
  const handleRequestSort = (property: keyof RouteGroup) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const generateDates = (createdAtDay: Date, oneMonthFromNow: Date, routeType: string): Date[] => {
    const dates: Date[] = [];
    let currentIterationDate = new Date(createdAtDay);
    // console.log('generateDates - start', { createdAtDay, oneMonthFromNow, routeType, currentIterationDate });

    while (currentIterationDate <= oneMonthFromNow) {
      if (routeType === 'weekly') {
        dates.push(new Date(currentIterationDate));
        // console.log('generateDates - weekly', { currentIterationDate });
        currentIterationDate.setDate(currentIterationDate.getDate() + 7);
      } else if (routeType === 'biweekly') {
        dates.push(new Date(currentIterationDate));
        // console.log('generateDates - biweekly', { currentIterationDate });
        currentIterationDate.setDate(currentIterationDate.getDate() + 14);
      }
    }

    // console.log('generateDates - end', { dates });
    return dates;
  };

  const generateMonthlyDates = (dayOfWeek: number, weekOfMonth: number, oneMonthFromNow: Date, routeType: string): Date[] => {
    const dates: Date[] = [];
    let currentIterationDate = new Date();
    // console.log('generateMonthlyDates - start', { dayOfWeek, weekOfMonth, oneMonthFromNow, routeType, currentIterationDate });
    // Calculate the first date based on dayOfWeek and weekOfMonth
    const currentDayOfWeek = currentIterationDate.getDay();
    const currentDay = currentIterationDate.getDate();
    const currentMonth = currentIterationDate.getMonth();
    const currentYear = currentIterationDate.getFullYear();
    const currentWeekOfMonth = Math.ceil(currentDay / 7);

    // Determine if the first date should be in the current month or the next
    if (currentWeekOfMonth > weekOfMonth || (currentWeekOfMonth === weekOfMonth && currentDayOfWeek > dayOfWeek)) {
        // Move to the next month
        currentIterationDate = new Date(currentYear, currentMonth + 1, 1);
    } else {
        // Stay in the current month
        currentIterationDate = new Date(currentYear, currentMonth, 1);
    }
    // Calculate the first date of the target week
    currentIterationDate.setDate((weekOfMonth - 1) * 7 + 1);
    while (currentIterationDate.getDay() !== dayOfWeek) {
      currentIterationDate.setDate(currentIterationDate.getDate() + 1);
    }
    while (currentIterationDate <= oneMonthFromNow) {
      dates.push(new Date(currentIterationDate));
      // console.log('generateMonthlyDates - monthly', { currentIterationDate });

      if (routeType === 'monthly') {
        currentIterationDate.setMonth(currentIterationDate.getMonth() + 1);
      } else if (routeType === 'bimonthly') {
        currentIterationDate.setMonth(currentIterationDate.getMonth() + 2);
      } else {
        currentIterationDate.setDate(currentIterationDate.getDate() + 1);
      }
    }

    // console.log('generateMonthlyDates - end', { dates });
    return dates;
  };

  const handleCreateRoutesUnion = useCallback(async () => {
    setShowNotification(true); // Show the notification popup

    try {
      const currentDate = new Date();
      const startDate = new Date(currentDate);
      startDate.setDate(startDate.getDate() + 1);
      const oneMonthFromNow = new Date(startDate);
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

      const routeGroupsRef = collection(db, 'routeGroups');
      const routeGroupsQuery = query(
        routeGroupsRef,
        where('routeType', 'in', ['weekly', 'biweekly', 'monthly', 'bimonthly'])
      );
      const routeGroupsSnapshot = await getDocs(routeGroupsQuery);

      for (const routeGroupDoc of routeGroupsSnapshot.docs) {
        const routesUnionQuery = query(
          collection(db, `routeGroups/${routeGroupDoc.id}/routesUnion`),
          where('date', '>=', startOfDay(startDate)),
          where('date', '<=', endOfDay(oneMonthFromNow))
        );
        const routesUnionSnapshot = await getDocs(routesUnionQuery);

        if (routesUnionSnapshot.empty) {
          const routeGroupData = routeGroupDoc.data() as RouteGroup;
          const routeType = routeGroupData.routeType;

          const routesRef = collection(db, `routeGroups/${routeGroupDoc.id}/routes`);
          const routesSnapshot = await getDocs(routesRef);
          const routes: Route[] = routesSnapshot.docs.map(routeDoc => ({
            id: routeDoc.id,
            ...routeDoc.data()
          } as Route));

          const createdAtTimestamp = routeGroupData.createdAt;
          const createdAt = createdAtTimestamp instanceof Timestamp ? createdAtTimestamp.toDate() : new Date(createdAtTimestamp);
          // console.log('Main - createdAt', { createdAt });

          if (isNaN(createdAt.getTime())) {
            // console.error('Invalid createdAt date:', routeGroupData.createdAt);
            return;
          }

          const datesToCreate = [];
          let currentIterationDate = new Date(startDate);
          // console.log('Main - start', { routeType, routes, createdAt, startDate, oneMonthFromNow });

          while (currentIterationDate <= oneMonthFromNow) {
            if (routeType === 'weekly' || routeType === 'biweekly') {
              const dates = generateDates(createdAt, oneMonthFromNow, routeType);
              if (dates.some(date =>
                date.getDate() === currentIterationDate.getDate() &&
                date.getMonth() === currentIterationDate.getMonth() &&
                date.getFullYear() === currentIterationDate.getFullYear()
              )) {
                datesToCreate.push(new Date(currentIterationDate));
              }
              currentIterationDate.setDate(currentIterationDate.getDate() + 1);
            } else if (routeType === 'monthly' || routeType === 'bimonthly') {
              const dayOfWeek = routeGroupData.dayOfWeek ?? 0; // Default to Sunday if undefined
              const weekOfMonth = routeGroupData.weekOfMonth ?? 1; // Default to the first week if undefined

              const dates = generateMonthlyDates(dayOfWeek, weekOfMonth, oneMonthFromNow, routeType);

              // console.log('Main - generated dates', { dates, currentIterationDate });

              if (dates.some(date =>
                date.getDate() === currentIterationDate.getDate() &&
                date.getMonth() === currentIterationDate.getMonth() &&
                date.getFullYear() === currentIterationDate.getFullYear()
              )) {
                datesToCreate.push(new Date(currentIterationDate));
              }
              // if (routeType === 'monthly') {
              //   currentIterationDate.setMonth(currentIterationDate.getMonth() + 1);
              // } else if (routeType === 'bimonthly') {
              //   currentIterationDate.setMonth(currentIterationDate.getMonth() + 2);
              // } else {
              //   currentIterationDate.setDate(currentIterationDate.getDate() + 1);
              // }
              currentIterationDate.setDate(currentIterationDate.getDate() + 1);
            }
          }

          // console.log('Main - datesToCreate', { datesToCreate });

          const batch = writeBatch(db);
          for (const date of datesToCreate) {
            const newRoutesUnionRef = doc(collection(db, `routeGroups/${routeGroupDoc.id}/routesUnion`));
            batch.set(newRoutesUnionRef, {
              routes,
              date: date,
              isUnificationFlag: false,
            });
          }
          await batch.commit();
          // console.log('Main - batch commit complete');
        }
      }

      setLoading(false);
    } catch (error) {
      // console.error('Error creating routes union:', error);
      setLoading(false);
    } finally {
      setShowNotification(false);
      window.location.reload();
    }
  }, []);

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: 'contents', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <FormControlLabel
            control={
              <Switch
                checked={tabValue === 1}
                onChange={() => setTabValue(tabValue === 0 ? 1 : 0)}
                color="primary"
                sx={{
                  '&.MuiSwitch-root': {
                    '& .MuiSwitch-thumb': {
                      backgroundColor: 'rgb(0, 180, 216) !important',
                    }
                  },
                }}
              />
            }
            label={
              <span style={{ fontWeight: 'bold' }}>
                {tabValue === 0 ? "Tabla de Rutas Perzonalizadas" : "Lista de Cartas"}
              </span>
            }
            sx={{ mb: 2, marginBottom: '21px' }}
          />
          <Button
            onClick={handleCreateRoutesUnion}
            color="primary"
            sx={{
              color: 'white',
              backgroundColor: '#87CEEB', // Color de fondo atractivo
              borderRadius: '8px', // Bordes redondeados
              padding: '10px', // Espaciado interno
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)', // Sombra para efecto de elevación
              '&:hover': {
                backgroundColor: '#5CACEE', // Color de fondo al pasar el mouse
                boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.3)', // Sombra más intensa al pasar el mouse
              },
              '&:active': {
                backgroundColor: '#4DA3D1', // Color de fondo al hacer clic
                transform: 'scale(0.95)', // Efecto de pulsación al hacer clic
              },
              fontWeight: 'bold',
              transition: 'background-color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease', // Transiciones suaves
            }}
            title="Actualizar datos" // Tooltip para mejorar la accesibilidad
          >
            <RefreshIcon fontSize="large" sx={{ mr: '6px', fontSize: '1.5rem' }} /> {/* Ícono más grande */}
            Crear Rutas Perzonalizadas Disponibles
          </Button>
        </Box>

        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
            }}
            sx={{ alignItems: 'left' }}
          />

          <Box sx={{ alignItems: 'right' }}>
            <Tooltip title="Filtrar">
              <IconButton onClick={handleFilterDialogOpen}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Seleccionar columnas">
              <IconButton onClick={handleColumnSelectionDialogOpen}>
                <ViewColumnIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Imprimir">
              <IconButton onClick={handlePrint}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
            {/* <Tooltip title="Descargar CSV">
              <IconButton onClick={handleDownloadCSV}>
                <GetAppIcon />
              </IconButton>
            </Tooltip> */}
          </Box>
        </Box>
      </Box>
      {/* Mostrar filtros seleccionados */}
      <Box sx={{ mb: 2, marginTop: '13px' }}>
        {selectedRouteTypes.map((type) => (
          <Chip
            key={type}
            label={type === 'weekly' ? 'Semanal' :
              type === 'biweekly' ? 'Bisemanal' :
                type === 'monthly' ? 'Mensual' : 'Bimensual'}
            onDelete={() => {
              const newSelected = selectedRouteTypes.filter(t => t !== type);
              setSelectedRouteTypes(newSelected);
              handleFilterChange('routeType', newSelected); // Actualiza los filtros
            }}
            sx={{ mr: 1, backgroundColor: 'rgb(0, 180, 216)', color: 'white' }}
          />
        ))}
      </Box>

      {
        tabValue === 0 && (
          // Vista de Tabla
          <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
            <Table sx={{ minWidth: 650 }} aria-label="route groups table">
              <TableHead sx={{ backgroundColor: 'rgb(0, 180, 216)' }}>
                <TableRow>
                  {Array.from(visibleColumns).map((column) => (
                    <TableCell key={column}>
                      <TableSortLabel
                        active={orderBy === column}
                        direction={orderBy === column ? order : 'asc'}
                        onClick={() => handleRequestSort(column as keyof RouteGroup)}
                        sx={{ color: 'white', fontWeight: 'bold' }}
                      >
                        {columnNames[column as keyof typeof columnNames]}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRoutesUnion.map((routeUnionObj) => {
                  const nextDelivery = routeUnionObj.date; // date is at index 1
                  return (
                    <TableRow
                      key={routeUnionObj.groupId}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(0, 180, 216, 0.1)',
                          '& .edit-icon': {
                            opacity: 1,
                          },
                        },
                      }}
                    >
                      {visibleColumns.has('name') && <TableCell>{routeUnionObj.groupName}</TableCell>}
                      {visibleColumns.has('routeType') && (
                        <TableCell>
                          {routeUnionObj.routeType === 'weekly' ? 'Semanal' :
                            routeUnionObj.routeType === 'biweekly' ? 'Bisemanal' :
                              routeUnionObj.routeType === 'monthly' ? 'Mensual' : 'Bimensual'}
                        </TableCell>
                      )}
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      {/* {visibleColumns.has('createdAt') && (
                        <TableCell>
                          {(routeUnionObj.routeType === 'weekly' || routeUnionObj.routeType === 'biweekly') &&
                            routeUnionObj.createdAt.toLocaleDateString()}
                        </TableCell>
                      )} */}
                      <TableCell></TableCell>
                      {/* {visibleColumns.has('nextDelivery') && <TableCell>{nextDelivery.toLocaleDateString()}</TableCell>} */}
                      <TableCell>
                        {/* <IconButton
                          onClick={() => handleOpen(routeUnionObj.routeUnion)}
                          sx={{
                            color: '#00b4d8',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 180, 216, 0.1)',
                            },
                          }}
                        >
                          <EditIcon className="edit-icon" sx={{ color: '#00b4d8', marginRight: '8px' }} />
                          <Typography variant="button" sx={{ color: '#00b4d8' }}></Typography>
                        </IconButton> */}
                        {/* <IconButton color="error" onClick={() => routeUnionObj.groupId && handleDelete(routeUnionObj.groupId)}>
                          <DeleteIcon />
                        </IconButton> */}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      }

      {
        tabValue === 1 && (
          // Vista de Lista de Cartas
          <Grid container spacing={3}>
            {routesUnion.map((routeUnionObj, index) => (
              <Grid item xs={12} sm={6} md={4} key={`${routeUnionObj.groupId}-${index}`}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent>
                    <Typography variant="h6" component="div" gutterBottom>
                      {routeUnionObj.groupName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tipo de Ruta: {routeUnionObj.routeType === 'weekly' ? 'Semanal' :
                        routeUnionObj.routeType === 'biweekly' ? 'Bisemanal' :
                          routeUnionObj.routeType === 'monthly' ? 'Mensual' : 'Bimensual'}
                    </Typography>
                    {/* {(routeUnionObj.routeType === 'weekly' || routeUnionObj.routeType === 'biweekly' || routeUnionObj.routeType === 'monthly') || routeUnionObj.routeType === 'bimonthly' && (
                      <Typography variant="body2" color="text.secondary">
                        Fecha de Entrega: {routeUnionObj.routeUnion && routeUnionObj.routeUnion.length > 0 ? new Date(routeUnionObj.routeUnion[0].date).toLocaleDateString() : 'N/A'}
                      </Typography>
                    )} */}

                  </CardContent>
                  <CardActions>
                    {/* <Button size="small" onClick={() => handleOpen(routeUnionObj.routeUnion)}>Editar</Button> */}
                    <Button size="small" color="error" onClick={() => routeUnionObj.groupId && handleDelete(routeUnionObj.groupId)}>Eliminar</Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )
      }

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredRoutesUnion.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        sx={{
          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
            margin: 0,
          },
          '.MuiTablePagination-toolbar': {
            paddingLeft: 2,
            paddingRight: 2,
          },
        }}
      />


      <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#87CEEB', color: 'white', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}> {/* Añadido para espaciar los elementos */}
            <Avatar sx={{
              bgcolor: '#FFA500',
              color: 'white',
              width: 47,
              height: 47,
              fontSize: 27
            }}>
              {currentRouteGroup?.routes.length} {/* Aquí puedes usar el total de pedidos que desees mostrar */}
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white', ml: 1 }}>
              Total de Pedidos
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 0.7, textAlign: 'center', ml: '3em' }}> {/* Añadido para centrar el texto */}
            {currentRouteGroup?.id ? 'Editar la Automatización' : 'Agregar Grupo de Rutas'}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* <Tabs
            value={0}
            aria-label="route group tabs"
            sx={{
              mb: 2,
              bgcolor: 'background.paper', // Fondo de las pestañas
              borderRadius: 2, // Bordes redondeados
              boxShadow: 1, // Sombra para un efecto elevado
              '& .MuiTab-root': {
                color: '#87CEEB', // Color de texto por defecto
                '&.Mui-selected': {
                  color: 'white', // Color de texto cuando está seleccionado
                  backgroundColor: '#5CACEE', // Fondo cuando está seleccionado
                  borderRadius: 2, // Bordes redondeados para la pestaña seleccionada
                },
                '&:hover': {
                  backgroundColor: 'rgba(92, 202, 238, 0.2)', // Efecto hover
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#5CACEE', // Color del indicador
              },
            }}
          >
            <Tab label="Configuración de la Automatización" />
          </Tabs> */}
          <Grid container spacing={3}>
            <Grid item xs={9}>
              <Box sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <SectionTitle variant="h6">
                    <IconWrapper>
                      <FormatListBulleted />
                      <Typography variant="h6" sx={{ ml: '1rem' }}>
                        Lista de Pedidos
                      </Typography>
                    </IconWrapper>
                  </SectionTitle>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addRoute}
                    variant="contained"
                    sx={{
                      mt: 2,
                      backgroundColor: '#00b4d8',
                      '&:hover': {
                        backgroundColor: '#0077be',
                      },
                    }}
                  >
                    Agregar Pedido
                  </Button>
                </Box>

                {currentRouteGroup?.routes.map((route, index) => (
                  <Paper
                    key={route.id || `route-${index}`}
                    elevation={1}
                    sx={{
                      mb: 2,
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: 'white',
                      borderLeft: `6px solid ${index === 0 ? '#FFA500' : '#00b4d8'}`,
                      transition: 'all 0.3s ease',
                      '&:hover': { transform: 'translateY(-4px)' }
                    }}
                  >
                    <Avatar sx={{
                      bgcolor: (index === 0 || index === currentRouteGroup.routes.length - 1) ? '#FFA500' : '#00b4d8',
                      color: 'white',
                      mr: 2
                    }}>
                      {index + 1}
                    </Avatar>

                    <Tooltip title={`Tipo de entrega: ${clients.find(c => c.id === route.clientId)?.tipo_entrega || 'No especificado'}`}>
                      <IconButton sx={{ mr: 2 }}>
                        {clients.find(c => c.id === route.clientId)?.tipo_entrega === 'Recolección'
                          ? <RecoleccionIcon sx={{ color: 'green' }} />
                          : <OllinIcon sx={{ color: '#00b4d8' }} />}
                      </IconButton>
                    </Tooltip>

                    <FormControl fullWidth margin="dense" sx={{ mr: '1rem' }}>
                      <InputLabel
                        sx={{ backgroundColor: 'white', padding: '0 4px' }} // Estilo agregado al InputLabel
                      >Cliente</InputLabel>
                      <Select
                        value={route.clientId || ''}
                        onChange={(e) => handleRouteChange(index, 'clientId', e.target.value as string)}
                      >
                        <MenuItem value="" disabled>
                          <em>Seleccione un cliente</em>
                        </MenuItem>
                        {clients
                          .filter(client => !currentRouteGroup.routes.some(r => r.clientId === client.id && r !== route))
                          .map((client) => (
                            <MenuItem key={client.id} value={client.id}>{client.name}</MenuItem>
                          ))
                        }
                      </Select>
                    </FormControl>
                    {/* <FormControl sx={{ width: '222px', mr: 2 }}>
                      <InputLabel>Estado</InputLabel>
                      <Select
                        value={route.status || 'pending'}
                        onChange={(e) => handleRouteChange(index, 'status', e.target.value as 'pending' | 'completed' | 'cancelled')}
                      >
                        <MenuItem value="pending">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PendingActions sx={{ mr: 1, color: '#FFA500' }} />
                            Pendiente
                          </Box>
                        </MenuItem>
                        <MenuItem value="completed">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CheckCircle sx={{ mr: 1, color: '#4CAF50' }} />
                            Completado
                          </Box>
                        </MenuItem>
                        <MenuItem value="cancelled">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Cancel sx={{ mr: 1, color: '#F44336' }} />
                            Cancelado
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl> */}
                    <IconButton
                      onClick={() => handleCreateOrEditOrder(currentRouteGroup!, route)}
                      sx={{
                        mr: 1, // Reducido para acercar al botón de eliminar
                        border: 'none', // Quitar el borde
                        color: '#00b4d8',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 180, 216, 0.1)',
                        },
                      }}
                    >
                      {route.order && route.order.items && route.order.items.length > 0 ? (
                        <EditIcon /> // Icono para editar
                      ) : (
                        <AddIcon /> // Icono para crear
                      )}
                    </IconButton>
                    {/* <IconButton onClick={() => removeRoute(index)} sx={{ color: '#F44336' }}>
                      <DeleteIcon />
                    </IconButton> */}
                  </Paper>
                ))}

              </Box>
            </Grid>
            <Grid item xs={3}>
              <InfoSection elevation={3}>
                <SectionTitle variant="h6" sx={{ ml: '2rem' }}>
                  <IconWrapper>
                    <CalendarToday />
                    <Typography variant="h6" sx={{ ml: '2rem' }}>
                      Configuración de la Automatización
                    </Typography>
                  </IconWrapper>
                </SectionTitle>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <StyledFormControl fullWidth>
                      <TextField
                        label="Nombre"
                        value={currentRouteGroup?.name || ''}
                        onChange={(e) => setCurrentRouteGroup(currentRouteGroup ? { ...currentRouteGroup, name: e.target.value } : null)}
                      />
                    </StyledFormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <StyledFormControl fullWidth>
                      <InputLabel
                        sx={{ backgroundColor: 'white', padding: '0 4px' }} // Estilo agregado al InputLabel
                      >Tipo de Ruta</InputLabel>
                      <Select
                        value={currentRouteGroup?.routeType || 'weekly'}
                        onChange={(e) => handleRouteTypeChange(e.target.value as 'weekly' | 'biweekly' | 'monthly' | 'bimonthly')}
                      >
                        <MenuItem value="weekly">Semanal</MenuItem>
                        <MenuItem value="biweekly">Bisemanal</MenuItem>
                        <MenuItem value="monthly">Mensual</MenuItem>
                        <MenuItem value="bimonthly">Bimensual</MenuItem>
                        <MenuItem value="Unification">Unificación</MenuItem>
                      </Select>
                    </StyledFormControl>
                  </Grid>
                  {(currentRouteGroup?.routeType === 'monthly' || currentRouteGroup?.routeType === 'bimonthly') && (
                    <>
                      <Grid item xs={12}>
                        <StyledFormControl fullWidth>
                          <InputLabel
                            sx={{ backgroundColor: 'white', padding: '0 4px' }} // Estilo agregado al InputLabel
                          >Día de la Semana</InputLabel>
                          <Select
                            value={currentRouteGroup?.dayOfWeek || 1}
                            onChange={(e) => setCurrentRouteGroup(currentRouteGroup ? { ...currentRouteGroup, dayOfWeek: e.target.value as number } : null)}
                          >
                            {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                              <MenuItem key={day} value={day}>{getDayOfWeekName(day)}</MenuItem>
                            ))}
                          </Select>
                        </StyledFormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <StyledFormControl fullWidth>
                          <InputLabel
                            sx={{ backgroundColor: 'white', padding: '0 4px' }} // Estilo agregado al InputLabel
                          >Semana del Mes</InputLabel>
                          <Select
                            value={currentRouteGroup?.weekOfMonth || 1}
                            onChange={(e) => setCurrentRouteGroup(currentRouteGroup ? { ...currentRouteGroup, weekOfMonth: e.target.value as number } : null)}
                          >
                            {[1, 2, 3, 4, 5].map((week) => (
                              <MenuItem key={week} value={week}>{week}</MenuItem>
                            ))}
                          </Select>
                        </StyledFormControl>
                      </Grid>
                    </>
                  )}
                  {(currentRouteGroup?.routeType === 'weekly' || currentRouteGroup?.routeType === 'biweekly') && (
                    <Grid item xs={12}>
                      <StyledFormControl fullWidth>
                        <DatePicker
                          selected={currentRouteGroup?.createdAt}
                          onChange={handleCreatedAtChange}
                          customInput={
                            <TextField
                              fullWidth
                              label="Fecha de inicio"
                              InputProps={{
                                startAdornment: (
                                  <Today style={{ color: '#00b4d8', marginRight: '8px' }} />
                                ),
                              }}
                            />
                          }
                        />
                      </StyledFormControl>
                    </Grid>
                  )}
                </Grid>
              </InfoSection>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', px: 3, py: 2, backgroundColor: 'rgba(135, 206, 235, 0.1)' }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              color: '#87CEEB',
              borderColor: '#87CEEB',
              '&:hover': {
                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                borderColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
              mr: 2,
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!isFormValid()}
            sx={{
              backgroundColor: '#87CEEB',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5CACEE',
              },
              '&:disabled': {
                backgroundColor: 'rgba(135, 206, 235, 0.5)',
                color: 'rgba(255, 255, 255, 0.7)',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <OrderCreationModalAutomatic
        open={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        inventoryItems={inventoryItems}
        existingOrder={currentRoute?.order ?? undefined}
        currentRouteGroup={currentRouteGroup || undefined} // Añadido
        currentRoute={currentRoute || undefined} // Añadido
        onOrderCreated={handleOrderCreated}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}
        fullScreen={isMobile}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
          },
        }}>
        <DialogTitle sx={{
          backgroundColor: '#87CEEB',
          color: 'white',
          fontWeight: 'bold'
        }}>Confirmar Eliminación</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 4, paddingTop: '29px !important' }}>
          <Box sx={{
            backgroundColor: 'red',
            borderRadius: '50%',
            padding: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 2
          }}>
            <CloseIcon sx={{ color: 'white', fontSize: 40 }} />
          </Box>
          <Typography variant="h6" align="center" sx={{ color: '#333', fontWeight: 'bold' }}>
            ¿Está seguro de que desea eliminar esta Ruta Perzonalizada?
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          justifyContent: 'center',
          px: 3,
          py: 2,
          backgroundColor: 'rgba(135, 206, 235, 0.1)'
        }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              color: '#87CEEB',
              borderColor: '#87CEEB',
              '&:hover': {
                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                borderColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
              mr: 2,
            }}
          >
            Cancelar
          </Button>
          <Button onClick={confirmDelete}
            variant="contained"
            sx={{
              backgroundColor: '#87CEEB',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgb(255, 0, 0)',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 0, 0, 0.5)',
                color: 'rgba(255, 255, 255, 0.7)',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
            }}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={filterDialogOpen}
        onClose={handleFilterDialogClose}
        maxWidth="md"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#87CEEB', color: 'white', fontWeight: 'bold' }}>
          Filtrar Grupos de Rutas
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={6} sx={{ ml: 39 }} >
              {/* Aquí va el resto de tu código, incluyendo el ComboBox */}
              <FormControl fullWidth variant="outlined" sx={{ mt: 2 }} >
                {/* <InputLabel sx={{ textAlign: 'center', width: '100%' }}>Tipo de Ruta</InputLabel>
                <Select
                  multiple
                  value={filters.routeType || []}
                  onChange={(e) => {
                    handleFilterChange('routeType', e.target.value as string[]);
                    setSelectedRouteTypes(e.target.value as string[]); // Actualiza el estado aquí
                  }}
                  input={<OutlinedInput label="Tipo de Ruta" />}
                  renderValue={(selected) => (selected as string[]).map(type => {
                    switch (type) {
                      case 'weekly': return 'Semanal';
                      case 'biweekly': return 'Bisemanal';
                      case 'monthly': return 'Mensual';
                      case 'bimonthly': return 'Bimensual';
                      default: return type;
                    }
                  }).join(', ')} // Traducción al español
                >
                  {['weekly', 'biweekly', 'monthly', 'bimonthly'].map((type) => (
                    <MenuItem key={type} value={type}>
                      <Checkbox checked={(filters.routeType || []).indexOf(type) > -1} />
                      <ListItemText primary={type === 'weekly' ? 'Semanal' :
                        type === 'biweekly' ? 'Bisemanal' :
                          type === 'monthly' ? 'Mensual' : 'Bimensual'} />
                    </MenuItem>
                  ))}
                </Select> */}
                <FormLabel component="legend">Tipo de Ruta</FormLabel>
                <FormGroup>
                  {['weekly', 'biweekly', 'monthly', 'bimonthly'].map(type => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox
                          checked={selectedRouteTypes.includes(type)}
                          onChange={(event) => {
                            const newSelected = event.target.checked
                              ? [...selectedRouteTypes, type]
                              : selectedRouteTypes.filter(t => t !== type);
                            handleFilterChange('routeType', newSelected);
                          }}
                        />
                      }
                      label={type === 'weekly' ? 'Semanal' :
                        type === 'biweekly' ? 'Bisemanal' :
                          type === 'monthly' ? 'Mensual' : 'Bimensual'}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{
          justifyContent: 'center',
          px: 3,
          py: 2,
          backgroundColor: 'rgba(135, 206, 235, 0.1)'
        }}>
          <Button
            onClick={handleFilterDialogClose}
            variant="outlined"
            sx={{
              color: '#87CEEB',
              borderColor: '#87CEEB',
              '&:hover': {
                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                borderColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
              mr: 2,
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={applyFilters}
            variant="contained"
            sx={{
              backgroundColor: '#87CEEB',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
            }}
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={columnSelectionDialogOpen}
        onClose={handleColumnSelectionDialogClose}
        PaperProps={{
          sx: {
            width: '400px',
            maxHeight: '80vh',
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#87CEEB',
          color: 'white',
          fontWeight: 'bold'
        }}>
          Seleccionar Columnas
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2
          }}>
            {/* {['name', 'routeType', 'createdAt'].map((column) => ( */}
            {['name', 'routeType'].map((column) => (
              <FormControlLabel
                key={column}
                control={
                  <Checkbox
                    checked={visibleColumns.has(column)}
                    onChange={() => handleColumnToggle(column)}
                    sx={{
                      color: '#87CEEB',
                      '&.Mui-checked': {
                        color: '#87CEEB',
                      },
                    }}
                  />
                }
                label={columnNames[column as keyof typeof columnNames] || column}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(135, 206, 235, 0.08)',
                    borderRadius: '4px',
                  },
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', px: 3, py: 2, backgroundColor: 'rgba(135, 206, 235, 0.1)' }}>
          <Button
            onClick={handleColumnSelectionDialogClose}
            variant="outlined"
            sx={{
              color: '#87CEEB',
              borderColor: '#87CEEB',
              '&:hover': {
                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                borderColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {showNotification && (
        <>
          <DarkOverlay />
          <NotificationPopup>
            <AnimatedEmoji style={{ animationDelay: '0s' }}>✨</AnimatedEmoji>
            <Typography
              sx={{
                color: '#1a1a1a',
                fontWeight: 600,
                fontSize: '1.5rem',
                letterSpacing: '-0.02em',
              }}
            >
              Creando Rutas Perzonalizadas en un rango de 1 mes
            </Typography>
            <AnimatedEmoji style={{ animationDelay: '0.2s' }}>🎉</AnimatedEmoji>
          </NotificationPopup>
        </>
      )}

    </Box >
  );
};

export default RouteGroupsCRUD;